import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { API } from "../App";
import { toast } from "sonner";
import {
  ShoppingCart,
  Plus,
  Package,
  Truck,
  Clock,
  CheckCircle,
  XCircle,
  Trash2,
  Eye,
  Euro,
  AlertTriangle,
  Lightbulb,
  ArrowRight,
  FileText,
  Copy,
  Save,
  Undo2,
  Ban
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";

const statusConfig = {
  ordered: { label: "Bestellt", color: "bg-amber-100 text-amber-800", icon: Clock },
  delivered: { label: "Geliefert", color: "bg-emerald-100 text-emerald-800", icon: CheckCircle },
  cancelled: { label: "Storniert", color: "bg-slate-100 text-slate-800", icon: XCircle }
};

const urgencyConfig = {
  critical: { label: "Kritisch", color: "bg-red-100 text-red-800 border-red-300" },
  medium: { label: "Mittel", color: "bg-amber-100 text-amber-800 border-amber-300" },
  low: { label: "Niedrig", color: "bg-blue-100 text-blue-800 border-blue-300" }
};

const OrderForm = ({ products, suppliers, onSuccess, onClose, initialItems = [], onSaveAsTemplate }) => {
  const [supplier, setSupplier] = useState("");
  const [supplierId, setSupplierId] = useState("");
  const [notes, setNotes] = useState("");
  const [items, setItems] = useState(initialItems);
  const [saving, setSaving] = useState(false);
  const [templateName, setTemplateName] = useState("");
  const [showSaveTemplate, setShowSaveTemplate] = useState(false);

  const addItem = (productId) => {
    if (!productId || productId === "none") return;
    const product = products.find(p => p.id === productId);
    if (!product) return;
    
    if (items.find(i => i.product_id === productId)) {
      toast.error("Produkt bereits hinzugefügt");
      return;
    }
    
    setItems([...items, {
      product_id: productId,
      product_name: product.name,
      package_size: product.package_size || 1,
      package_price_net: product.package_price_net || 0,
      package_price_gross: product.package_price_gross || 0,
      quantity_packages: 1
    }]);
  };

  const updateQuantity = (productId, qty) => {
    setItems(items.map(item => 
      item.product_id === productId 
        ? { ...item, quantity_packages: Math.max(1, parseInt(qty) || 1) }
        : item
    ));
  };

  const removeItem = (productId) => {
    setItems(items.filter(item => item.product_id !== productId));
  };

  const totalNet = items.reduce((sum, item) => 
    sum + (item.package_price_net * item.quantity_packages), 0);
  const totalGross = items.reduce((sum, item) => 
    sum + (item.package_price_gross * item.quantity_packages), 0);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (items.length === 0) {
      toast.error("Bitte mindestens ein Produkt hinzufügen");
      return;
    }

    setSaving(true);
    try {
      await axios.post(`${API}/orders`, {
        supplier: supplier.trim() || (suppliers.find(s => s.id === supplierId)?.name || ""),
        notes: notes.trim(),
        items: items.map(i => ({
          product_id: i.product_id,
          quantity_packages: i.quantity_packages
        }))
      });
      toast.success("Bestellung erstellt");
      onClose();
      onSuccess();
    } catch (err) {
      toast.error("Fehler beim Erstellen");
    } finally {
      setSaving(false);
    }
  };

  const handleSaveTemplate = async () => {
    if (!templateName.trim()) {
      toast.error("Bitte Vorlagenname eingeben");
      return;
    }
    if (items.length === 0) {
      toast.error("Bitte mindestens ein Produkt hinzufügen");
      return;
    }

    try {
      await axios.post(`${API}/order-templates`, {
        name: templateName.trim(),
        supplier_id: supplierId || null,
        notes: notes.trim(),
        items: items.map(i => ({
          product_id: i.product_id,
          quantity_packages: i.quantity_packages
        }))
      });
      toast.success("Vorlage gespeichert");
      setShowSaveTemplate(false);
      setTemplateName("");
      if (onSaveAsTemplate) onSaveAsTemplate();
    } catch (err) {
      toast.error("Fehler beim Speichern");
    }
  };

  // Sort products alphabetically
  const sortedProducts = [...products].sort((a, b) => a.name.localeCompare(b.name));

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Lieferant (auswählen)</Label>
          <Select value={supplierId} onValueChange={(v) => {
            setSupplierId(v);
            if (v) {
              const s = suppliers.find(sup => sup.id === v);
              if (s) setSupplier(s.name);
            }
          }}>
            <SelectTrigger data-testid="order-supplier-select">
              <SelectValue placeholder="Lieferant wählen" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">-- Kein Lieferant --</SelectItem>
              {suppliers.map((s) => (
                <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label>oder manuell eingeben</Label>
          <Input
            value={supplier}
            onChange={(e) => setSupplier(e.target.value)}
            placeholder="z.B. Metro, Edeka"
            data-testid="order-supplier-input"
          />
        </div>
      </div>

      <div className="space-y-2">
        <Label>Notizen</Label>
        <Input
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Optional"
          data-testid="order-notes-input"
        />
      </div>

      <div className="space-y-2">
        <Label>Produkt hinzufügen</Label>
        <Select onValueChange={addItem}>
          <SelectTrigger data-testid="order-product-select">
            <SelectValue placeholder="Produkt wählen" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="none">-- Produkt wählen --</SelectItem>
            {sortedProducts.map((p) => (
              <SelectItem key={p.id} value={p.id}>
                {p.name} ({p.package_size} Stk/VPE) - {p.package_price_net?.toFixed(2)}€ netto
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {items.length > 0 && (
        <div className="border rounded-md overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Produkt</TableHead>
                <TableHead className="text-center">VPE</TableHead>
                <TableHead className="text-center">Anzahl VPE</TableHead>
                <TableHead className="text-right">Summe Netto</TableHead>
                <TableHead className="w-10"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((item) => (
                <TableRow key={item.product_id}>
                  <TableCell className="font-medium">{item.product_name}</TableCell>
                  <TableCell className="text-center">{item.package_size} Stk</TableCell>
                  <TableCell className="text-center">
                    <Input
                      type="number"
                      min="1"
                      value={item.quantity_packages}
                      onChange={(e) => updateQuantity(item.product_id, e.target.value)}
                      className="w-20 mx-auto text-center"
                    />
                  </TableCell>
                  <TableCell className="text-right font-mono">
                    {(item.package_price_net * item.quantity_packages).toFixed(2)}€
                  </TableCell>
                  <TableCell>
                    <Button
                      type="button"
                      size="icon"
                      variant="ghost"
                      onClick={() => removeItem(item.product_id)}
                    >
                      <Trash2 className="w-4 h-4 text-slate-400" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      {items.length > 0 && (
        <div className="bg-slate-50 rounded-md p-4 space-y-2">
          <div className="flex justify-between text-sm">
            <span>Summe Netto:</span>
            <span className="font-mono font-bold">{totalNet.toFixed(2)}€</span>
          </div>
          <div className="flex justify-between text-lg font-bold">
            <span>Summe Brutto:</span>
            <span className="font-mono text-[#DC2626]">{totalGross.toFixed(2)}€</span>
          </div>
        </div>
      )}

      <div className="flex gap-3 pt-2">
        <Button type="button" variant="outline" onClick={onClose} className="flex-1">
          Abbrechen
        </Button>
        {items.length > 0 && (
          <Button 
            type="button" 
            variant="outline"
            onClick={() => setShowSaveTemplate(true)}
            className="flex items-center gap-2"
          >
            <Save className="w-4 h-4" />
            Als Vorlage
          </Button>
        )}
        <Button 
          type="submit" 
          disabled={saving || items.length === 0}
          className="flex-1 bg-[#DC2626] hover:bg-[#B91C1C]"
          data-testid="order-save-btn"
        >
          {saving ? "Speichern..." : "Bestellung erstellen"}
        </Button>
      </div>

      {/* Save as Template Dialog */}
      <Dialog open={showSaveTemplate} onOpenChange={setShowSaveTemplate}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Als Vorlage speichern</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            <div className="space-y-2">
              <Label>Vorlagenname</Label>
              <Input
                value={templateName}
                onChange={(e) => setTemplateName(e.target.value)}
                placeholder="z.B. Wöchentliche Snacks"
                autoFocus
              />
            </div>
            <div className="text-sm text-slate-500">
              Diese Vorlage enthält {items.length} Produkt(e) und kann für zukünftige Bestellungen verwendet werden.
            </div>
            <div className="flex gap-3">
              <Button variant="outline" onClick={() => setShowSaveTemplate(false)} className="flex-1">
                Abbrechen
              </Button>
              <Button onClick={handleSaveTemplate} className="flex-1 bg-rose-600 hover:bg-rose-700">
                Vorlage speichern
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </form>
  );
};

const OrderDetailDialog = ({ orderId, onClose }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (orderId) {
      fetchOrder();
    }
  }, [orderId]);

  const fetchOrder = async () => {
    try {
      const res = await axios.get(`${API}/orders/${orderId}`);
      setData(res.data);
    } catch (err) {
      toast.error("Fehler beim Laden");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div className="py-8 text-center">Laden...</div>;
  }

  if (!data) return null;

  const { order, items } = data;

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <span className="text-sm text-slate-500">Lieferant</span>
          <p className="font-medium">{order.supplier || "-"}</p>
        </div>
        <div>
          <span className="text-sm text-slate-500">Datum</span>
          <p className="font-medium">
            {new Date(order.order_date).toLocaleDateString('de-DE')}
          </p>
        </div>
        <div>
          <span className="text-sm text-slate-500">Status</span>
          <Badge className={statusConfig[order.status]?.color || ''}>
            {statusConfig[order.status]?.label || order.status}
          </Badge>
        </div>
        <div>
          <span className="text-sm text-slate-500">Notizen</span>
          <p className="font-medium">{order.notes || "-"}</p>
        </div>
      </div>

      <div className="border rounded-md overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Produkt</TableHead>
              <TableHead className="text-center">Menge</TableHead>
              <TableHead className="text-center">Stück</TableHead>
              <TableHead className="text-right">Netto</TableHead>
              <TableHead className="text-right">Brutto</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.map((item) => (
              <TableRow key={item.id}>
                <TableCell className="font-medium">{item.product_name}</TableCell>
                <TableCell className="text-center">{item.quantity_packages} VPE</TableCell>
                <TableCell className="text-center">{item.quantity_pieces} Stk</TableCell>
                <TableCell className="text-right font-mono">{item.total_net.toFixed(2)}€</TableCell>
                <TableCell className="text-right font-mono">{item.total_gross.toFixed(2)}€</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <div className="bg-slate-50 rounded-md p-4 space-y-2">
        <div className="flex justify-between">
          <span>Gesamt Netto:</span>
          <span className="font-mono font-bold">{order.total_net.toFixed(2)}€</span>
        </div>
        <div className="flex justify-between text-lg font-bold">
          <span>Gesamt Brutto:</span>
          <span className="font-mono text-[#DC2626]">{order.total_gross.toFixed(2)}€</span>
        </div>
      </div>

      <Button variant="outline" onClick={onClose} className="w-full">
        Schließen
      </Button>
    </div>
  );
};

const SuggestionCard = ({ suggestion, onAddToOrder }) => {
  const urgency = urgencyConfig[suggestion.urgency] || urgencyConfig.low;
  const hasSupplierPrices = suggestion.supplier_prices && suggestion.supplier_prices.length > 0;
  
  return (
    <div className={`border-2 rounded-lg p-4 ${urgency.color}`}>
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <h4 className="font-medium">{suggestion.product_name}</h4>
            {suggestion.urgency === "critical" && (
              <AlertTriangle className="w-4 h-4 text-red-600" />
            )}
          </div>
          {suggestion.category && (
            <span className="text-xs text-slate-500">{suggestion.category}</span>
          )}
          
          <div className="mt-2 grid grid-cols-3 gap-2 text-sm">
            <div>
              <span className="text-slate-500">Bestand:</span>
              <span className="font-mono font-bold ml-1">{suggestion.current_stock}</span>
            </div>
            <div>
              <span className="text-slate-500">Min:</span>
              <span className="font-mono ml-1">{suggestion.min_stock}</span>
            </div>
            <div>
              <span className="text-slate-500">Ziel:</span>
              <span className="font-mono ml-1">{suggestion.target_stock}</span>
            </div>
          </div>
          
          {/* Supplier Prices */}
          {hasSupplierPrices && (
            <div className="mt-3 pt-3 border-t border-slate-200">
              <div className="text-xs text-slate-500 mb-1">Lieferantenpreise:</div>
              <div className="space-y-1">
                {suggestion.supplier_prices.slice(0, 3).map((sp, idx) => (
                  <div 
                    key={sp.supplier_id} 
                    className={`flex justify-between text-sm ${idx === 0 ? 'font-bold text-emerald-700' : 'text-slate-600'}`}
                  >
                    <span className="flex items-center gap-1">
                      {idx === 0 && <Badge className="bg-emerald-500 text-white text-[10px] px-1">Günstigster</Badge>}
                      {sp.supplier_name}
                    </span>
                    <span className="font-mono">
                      {sp.package_price_net?.toFixed(2)}€/VPE
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
        
        <div className="text-right">
          <div className="text-lg font-heading font-bold">
            {suggestion.suggested_packages} VPE
          </div>
          <div className="text-xs text-slate-500">
            = {suggestion.suggested_pieces} Stück
          </div>
          {hasSupplierPrices && suggestion.cheapest_estimated_net ? (
            <div className="text-sm font-mono mt-1 text-emerald-700">
              ab ~{suggestion.cheapest_estimated_net.toFixed(2)}€
            </div>
          ) : (
            <div className="text-sm font-mono mt-1">
              ~{suggestion.estimated_cost_gross.toFixed(2)}€
            </div>
          )}
          <Button
            size="sm"
            className="mt-2 bg-[#DC2626] hover:bg-[#B91C1C]"
            onClick={() => onAddToOrder(suggestion)}
          >
            <Plus className="w-3 h-3 mr-1" />
            Bestellen
          </Button>
        </div>
      </div>
    </div>
  );
};

const OrdersPage = () => {
  const [orders, setOrders] = useState([]);
  const [products, setProducts] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [suggestions, setSuggestions] = useState(null);
  const [loading, setLoading] = useState(true);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [viewOrderId, setViewOrderId] = useState(null);
  const [deleteOrder, setDeleteOrder] = useState(null);
  const [deleteTemplate, setDeleteTemplate] = useState(null);
  const [initialOrderItems, setInitialOrderItems] = useState([]);
  const [activeTab, setActiveTab] = useState("suggestions");

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [ordersRes, productsRes, suggestionsRes, suppliersRes, templatesRes] = await Promise.all([
        axios.get(`${API}/orders`),
        axios.get(`${API}/products`),
        axios.get(`${API}/orders/suggestions`),
        axios.get(`${API}/suppliers`),
        axios.get(`${API}/order-templates`)
      ]);
      setOrders(ordersRes.data);
      setProducts(productsRes.data);
      setSuggestions(suggestionsRes.data);
      setSuppliers(suppliersRes.data);
      setTemplates(templatesRes.data);
    } catch (err) {
      toast.error("Fehler beim Laden");
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (orderId, newStatus) => {
    try {
      await axios.put(`${API}/orders/${orderId}`, { status: newStatus });
      if (newStatus === "delivered") {
        toast.success("Bestellung als geliefert markiert - Lager aktualisiert!");
      } else {
        toast.success("Status aktualisiert");
      }
      fetchData();
    } catch (err) {
      toast.error("Fehler beim Aktualisieren");
    }
  };

  const handleDelete = async () => {
    if (!deleteOrder) return;
    try {
      await axios.delete(`${API}/orders/${deleteOrder.id}`);
      toast.success("Bestellung gelöscht");
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Fehler beim Löschen");
    } finally {
      setDeleteOrder(null);
    }
  };

  const handleDeleteTemplate = async () => {
    if (!deleteTemplate) return;
    try {
      await axios.delete(`${API}/order-templates/${deleteTemplate.id}`);
      toast.success("Vorlage gelöscht");
      fetchData();
    } catch (err) {
      toast.error("Fehler beim Löschen");
    } finally {
      setDeleteTemplate(null);
    }
  };

  const handleUseTemplate = async (template) => {
    try {
      const res = await axios.get(`${API}/order-templates/${template.id}`);
      const templateData = res.data;
      
      // Convert template items to order items format
      const items = templateData.items.map(item => ({
        product_id: item.product_id,
        product_name: item.product_name,
        package_size: item.package_size,
        package_price_net: item.package_price_net,
        package_price_gross: item.package_price_gross,
        quantity_packages: item.quantity_packages
      }));
      
      setInitialOrderItems(items);
      setCreateDialogOpen(true);
    } catch (err) {
      toast.error("Fehler beim Laden der Vorlage");
    }
  };

  const handleCreateOrderFromTemplate = async (template) => {
    try {
      await axios.post(`${API}/order-templates/${template.id}/create-order`);
      toast.success("Bestellung aus Vorlage erstellt");
      fetchData();
      setActiveTab("history");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Fehler beim Erstellen");
    }
  };

  const handleAddSuggestionToOrder = (suggestion) => {
    const product = products.find(p => p.id === suggestion.product_id);
    if (!product) return;
    
    setInitialOrderItems([{
      product_id: suggestion.product_id,
      product_name: suggestion.product_name,
      package_size: suggestion.package_size,
      package_price_net: suggestion.package_price_net,
      package_price_gross: suggestion.package_price_gross,
      quantity_packages: suggestion.suggested_packages
    }]);
    setCreateDialogOpen(true);
  };

  const handleAddAllSuggestionsToOrder = () => {
    if (!suggestions?.suggestions?.length) return;
    
    const items = suggestions.suggestions.map(s => ({
      product_id: s.product_id,
      product_name: s.product_name,
      package_size: s.package_size,
      package_price_net: s.package_price_net,
      package_price_gross: s.package_price_gross,
      quantity_packages: s.suggested_packages
    }));
    
    setInitialOrderItems(items);
    setCreateDialogOpen(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#DC2626]" />
      </div>
    );
  }

  const totalOrdered = orders.filter(o => o.status === "ordered").length;
  const totalDelivered = orders.filter(o => o.status === "delivered").length;
  const totalSpentGross = orders
    .filter(o => o.status === "delivered")
    .reduce((sum, o) => sum + o.total_gross, 0);

  return (
    <div className="space-y-6" data-testid="orders-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-heading font-bold text-slate-900 tracking-tight">
            Bestellungen
          </h1>
          <p className="text-slate-500 mt-1">
            Einkaufshistorie und Bestellvorschläge
          </p>
        </div>
        <Dialog open={createDialogOpen} onOpenChange={(open) => {
          setCreateDialogOpen(open);
          if (!open) setInitialOrderItems([]);
        }}>
          <DialogTrigger asChild>
            <Button 
              className="bg-[#DC2626] hover:bg-[#B91C1C] btn-hover" 
              data-testid="add-order-btn"
              onClick={() => setInitialOrderItems([])}
            >
              <Plus className="w-4 h-4 mr-2" />
              Neue Bestellung
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-heading">Neue Bestellung</DialogTitle>
            </DialogHeader>
            <OrderForm 
              products={products}
              suppliers={suppliers}
              onSuccess={fetchData}
              onClose={() => {
                setCreateDialogOpen(false);
                setInitialOrderItems([]);
              }}
              initialItems={initialOrderItems}
              onSaveAsTemplate={fetchData}
            />
          </DialogContent>
        </Dialog>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
        <Card className="border-slate-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-amber-100 rounded-md flex items-center justify-center">
                <Clock className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <div className="text-sm text-slate-500">Offen</div>
                <div className="text-xl font-heading font-bold">{totalOrdered}</div>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-slate-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-emerald-100 rounded-md flex items-center justify-center">
                <Truck className="w-5 h-5 text-emerald-600" />
              </div>
              <div>
                <div className="text-sm text-slate-500">Geliefert</div>
                <div className="text-xl font-heading font-bold text-emerald-600">{totalDelivered}</div>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-slate-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-100 rounded-md flex items-center justify-center">
                <Euro className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <div className="text-sm text-slate-500">Ausgaben</div>
                <div className="text-xl font-heading font-bold text-blue-600">
                  {totalSpentGross.toFixed(2)}€
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className={`border-2 ${suggestions?.total_items > 0 ? 'border-amber-300 bg-amber-50' : 'border-slate-200'}`}>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded-md flex items-center justify-center ${suggestions?.total_items > 0 ? 'bg-amber-200' : 'bg-slate-100'}`}>
                <Lightbulb className={`w-5 h-5 ${suggestions?.total_items > 0 ? 'text-amber-700' : 'text-slate-400'}`} />
              </div>
              <div>
                <div className="text-sm text-slate-500">Vorschläge</div>
                <div className={`text-xl font-heading font-bold ${suggestions?.total_items > 0 ? 'text-amber-700' : 'text-slate-400'}`}>
                  {suggestions?.total_items || 0}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="suggestions" className="relative">
            <Lightbulb className="w-4 h-4 mr-2" />
            Vorschläge
            {suggestions?.total_items > 0 && (
              <span className="ml-2 bg-amber-500 text-white text-xs px-2 py-0.5 rounded-full">
                {suggestions.total_items}
              </span>
            )}
          </TabsTrigger>
          <TabsTrigger value="templates">
            <FileText className="w-4 h-4 mr-2" />
            Vorlagen
            {templates.length > 0 && (
              <span className="ml-2 bg-slate-500 text-white text-xs px-2 py-0.5 rounded-full">
                {templates.length}
              </span>
            )}
          </TabsTrigger>
          <TabsTrigger value="history">
            <ShoppingCart className="w-4 h-4 mr-2" />
            Historie
          </TabsTrigger>
        </TabsList>

        {/* Suggestions Tab */}
        <TabsContent value="suggestions" className="space-y-4">
          {suggestions?.suggestions?.length > 0 ? (
            <>
              <Card className="border-slate-200">
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <CardTitle className="font-heading flex items-center gap-2">
                      <AlertTriangle className="w-5 h-5 text-amber-600" />
                      Nachbestellung empfohlen
                    </CardTitle>
                    <div className="text-right">
                      <div className="text-sm text-slate-500">Geschätzte Kosten</div>
                      <div className="font-heading font-bold text-[#DC2626]">
                        {suggestions.total_estimated_gross.toFixed(2)}€
                      </div>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-3">
                  {suggestions.suggestions.map((suggestion) => (
                    <SuggestionCard 
                      key={suggestion.product_id}
                      suggestion={suggestion}
                      onAddToOrder={handleAddSuggestionToOrder}
                    />
                  ))}
                  
                  {suggestions.suggestions.length > 1 && (
                    <Button 
                      className="w-full bg-[#DC2626] hover:bg-[#B91C1C]"
                      onClick={handleAddAllSuggestionsToOrder}
                    >
                      <ShoppingCart className="w-4 h-4 mr-2" />
                      Alle {suggestions.total_items} Produkte bestellen
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  )}
                </CardContent>
              </Card>
            </>
          ) : (
            <Card className="border-slate-200">
              <CardContent className="py-12 text-center">
                <CheckCircle className="w-12 h-12 mx-auto text-emerald-500 mb-4" />
                <p className="text-lg font-medium text-emerald-700">Alles auf Lager!</p>
                <p className="text-slate-500 mt-1">
                  Alle Produkte sind über dem Mindestbestand
                </p>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* History Tab */}
        <TabsContent value="history">
          <Card className="border-slate-200">
            <CardHeader>
              <CardTitle className="font-heading flex items-center gap-2">
                <ShoppingCart className="w-5 h-5" />
                Bestellhistorie
              </CardTitle>
            </CardHeader>
            <CardContent>
              {orders.length > 0 ? (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Datum</TableHead>
                      <TableHead>Lieferant</TableHead>
                      <TableHead>Erstellt von</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Brutto</TableHead>
                      <TableHead className="text-right">Aktionen</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {orders.map((order) => {
                      const StatusIcon = statusConfig[order.status]?.icon || Clock;
                      return (
                        <TableRow key={order.id}>
                          <TableCell>
                            {new Date(order.order_date).toLocaleDateString('de-DE')}
                          </TableCell>
                          <TableCell>{order.supplier || "-"}</TableCell>
                          <TableCell className="text-slate-500">{order.user_name || "-"}</TableCell>
                          <TableCell>
                            <Badge className={statusConfig[order.status]?.color}>
                              <StatusIcon className="w-3 h-3 mr-1" />
                              {statusConfig[order.status]?.label}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-right font-mono font-bold">
                            {order.total_gross.toFixed(2)}€
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex justify-end gap-1 flex-wrap">
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => setViewOrderId(order.id)}
                                title="Details anzeigen"
                              >
                                <Eye className="w-4 h-4" />
                              </Button>
                              {order.status === "ordered" && (
                                <>
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    className="text-emerald-600 hover:text-emerald-700"
                                    onClick={() => handleStatusChange(order.id, "delivered")}
                                    title="Als geliefert markieren"
                                  >
                                    <Truck className="w-4 h-4" />
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    className="text-amber-600 hover:text-amber-700"
                                    onClick={() => handleStatusChange(order.id, "cancelled")}
                                    title="Stornieren"
                                  >
                                    <Ban className="w-4 h-4" />
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    className="text-slate-400 hover:text-rose-600"
                                    onClick={() => setDeleteOrder(order)}
                                    title="Löschen"
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </Button>
                                </>
                              )}
                              {order.status === "delivered" && (
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className="text-amber-600 hover:text-amber-700"
                                  onClick={() => handleStatusChange(order.id, "ordered")}
                                  title="Rückgängig (zurück auf Bestellt)"
                                >
                                  <Undo2 className="w-4 h-4" />
                                </Button>
                              )}
                              {order.status === "cancelled" && (
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className="text-blue-600 hover:text-blue-700"
                                  onClick={() => handleStatusChange(order.id, "ordered")}
                                  title="Reaktivieren"
                                >
                                  <Undo2 className="w-4 h-4" />
                                </Button>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              ) : (
                <div className="text-center py-12">
                  <ShoppingCart className="w-12 h-12 mx-auto text-slate-300 mb-4" />
                  <p className="text-slate-500">Noch keine Bestellungen</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Templates Tab */}
        <TabsContent value="templates" className="space-y-4">
          {templates.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {templates.map((template) => (
                <motion.div
                  key={template.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                >
                  <Card className="border-slate-200 hover:border-slate-300 transition-colors">
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between">
                        <div>
                          <h3 className="font-heading font-bold">{template.name}</h3>
                          {template.supplier_name && (
                            <p className="text-sm text-slate-500">{template.supplier_name}</p>
                          )}
                          <p className="text-sm text-slate-400 mt-1">
                            {template.items?.length || 0} Produkt(e)
                          </p>
                        </div>
                        <FileText className="w-8 h-8 text-slate-300" />
                      </div>
                      <div className="flex gap-2 mt-4">
                        <Button
                          size="sm"
                          variant="outline"
                          className="flex-1"
                          onClick={() => handleUseTemplate(template)}
                        >
                          <Copy className="w-3 h-3 mr-1" />
                          Anpassen
                        </Button>
                        <Button
                          size="sm"
                          className="flex-1 bg-rose-600 hover:bg-rose-700"
                          onClick={() => handleCreateOrderFromTemplate(template)}
                        >
                          <ShoppingCart className="w-3 h-3 mr-1" />
                          Bestellen
                        </Button>
                        <Button
                          size="icon"
                          variant="ghost"
                          onClick={() => setDeleteTemplate(template)}
                        >
                          <Trash2 className="w-4 h-4 text-slate-400" />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </div>
          ) : (
            <Card className="border-slate-200">
              <CardContent className="py-12 text-center">
                <FileText className="w-12 h-12 mx-auto text-slate-300 mb-4" />
                <p className="text-slate-500">Noch keine Bestellvorlagen</p>
                <p className="text-sm text-slate-400 mt-1">
                  Erstellen Sie eine Bestellung und speichern Sie sie als Vorlage
                </p>
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>

      {/* View Order Dialog */}
      <Dialog open={!!viewOrderId} onOpenChange={() => setViewOrderId(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-heading">Bestelldetails</DialogTitle>
          </DialogHeader>
          {viewOrderId && (
            <OrderDetailDialog 
              orderId={viewOrderId}
              onClose={() => setViewOrderId(null)}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Order Confirmation */}
      <AlertDialog open={!!deleteOrder} onOpenChange={() => setDeleteOrder(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Bestellung löschen?</AlertDialogTitle>
            <AlertDialogDescription>
              Möchten Sie diese Bestellung wirklich löschen?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Abbrechen</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDelete}
              className="bg-[#DC2626] hover:bg-[#B91C1C]"
            >
              Löschen
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Template Confirmation */}
      <AlertDialog open={!!deleteTemplate} onOpenChange={() => setDeleteTemplate(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Vorlage löschen?</AlertDialogTitle>
            <AlertDialogDescription>
              Möchten Sie die Vorlage "{deleteTemplate?.name}" wirklich löschen?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Abbrechen</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDeleteTemplate}
              className="bg-[#DC2626] hover:bg-[#B91C1C]"
            >
              Löschen
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default OrdersPage;
